import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useFormContext} from 'react-hook-form';
import {ReactComponent as CloseIcon} from '@icons/CloseModal.svg';
import {Badge, type BadgeVariant} from '@ui/Badge';
import {ButtonIcon, ButtonPrimary, ButtonSecondary} from '@ui/Button';
import {Checkbox} from '@ui/Checkbox';
import {Form} from '@ui/Form';
import {useAuthAxios} from '@hooks/useAuthAxios';
import {pluralUsers, getClientTimezone} from '@utils/tariffFormat';
import styles from './RenewSubscriptionModal.module.scss';

export type RenewSubscriptionVariant = 'basic' | 'business';

interface RenewSubscriptionModalProps {
    variant: RenewSubscriptionVariant;
    tariffId: number;
    periodDays: number;
    userCount: number;
    price: string;
    displayName?: string;
    onClose: () => void;
}

const AGREEMENT_RULE = {
    validate: (v: boolean) => v === true || 'Необходимо принять условия подписки',
};

const VARIANT_CONFIG: Record<
    RenewSubscriptionVariant,
    {badgeLabel: string; badgeVariant: BadgeVariant; title: string; summaryFallback: string}
> = {
    basic: {
        badgeLabel: 'Базовый тариф',
        badgeVariant: 'lavender',
        title: 'Продлить подписку',
        summaryFallback: 'Тариф Basic',
    },
    business: {
        badgeLabel: 'Корпоративный тариф',
        badgeVariant: 'neutral',
        title: 'Продлить подписку',
        summaryFallback: 'Тариф Business',
    },
};

const RenewSubscriptionModalForm = ({
    variant,
    tariffId,
    periodDays,
    userCount,
    price,
    displayName,
    onClose,
}: RenewSubscriptionModalProps) => {
    const {trigger, formState: {errors}} = useFormContext();
    const authAxios = useAuthAxios();
    const [isLoading, setIsLoading] = useState(false);
    const [payError, setPayError] = useState<string | null>(null);

    const config = VARIANT_CONFIG[variant];
    const rawAgreementMsg = errors.agreement?.message;
    const agreementErrorText =
        rawAgreementMsg == null ? '' : Array.isArray(rawAgreementMsg) ? rawAgreementMsg[0] : rawAgreementMsg;

    const userLabel = `${userCount} ${pluralUsers(userCount)}`;
    const summaryLabel = displayName?.trim() || config.summaryFallback;

    const handlePay = async () => {
        const ok = await trigger('agreement');
        if (!ok) {
            return;
        }

        if (isLoading) {
            return;
        }

        setPayError(null);
        setIsLoading(true);
        try {
            const {data} = await authAxios.post<{payment_url: string; payment_id: number}>(
                '/tariffs/renew',
                {
                    tariff_id: tariffId,
                    client_timezone: getClientTimezone(),
                },
            );

            if (!data.payment_url) {
                setPayError('Не удалось получить ссылку на оплату. Попробуйте позже.');
                setIsLoading(false);
                return;
            }

            window.location.href = data.payment_url;
        } catch (error: unknown) {
            const status = (error as {response?: {status?: number}})?.response?.status;
            if (status === 401) {
                window.location.href = '/login';
                return;
            }

            const message =
                (error as {response?: {data?: {message?: string}}})?.response?.data?.message ??
                (status === 422
                    ? 'Платёж невозможен. Свяжитесь с поддержкой.'
                    : 'Не удалось перейти к оплате. Попробуйте позже.');
            setPayError(message);
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className={styles.header}>
                <div className={styles.headerBody}>
                    <Badge
                        variant={config.badgeVariant}
                        density={variant === 'business' ? 'compact' : 'default'}
                    >
                        {config.badgeLabel}
                    </Badge>
                    <h2 className={styles.title}>{config.title}</h2>
                </div>
                <ButtonIcon
                    type="button"
                    className={styles.closeBtn}
                    onClick={onClose}
                    aria-label="Закрыть"
                >
                    <CloseIcon aria-hidden/>
                </ButtonIcon>
            </div>

            <div className={styles.body}>
                <div className={styles.summaryCard}>
                    <div className={styles.summaryInfo}>
                        <span className={styles.summaryLabel}>{summaryLabel}</span>
                        <p className={styles.summaryPrice}>{price}</p>
                        <span className={styles.summaryUsers}>{userLabel}</span>
                    </div>
                    <Badge variant="lavender" className={styles.summaryBadge}>{periodDays} дней</Badge>
                </div>

                <div className={styles.agreementWrap}>
                    <Checkbox name="agreement" validate={AGREEMENT_RULE} errorId="renew-agreement-error">
                        <span className={styles.agreementText}>
                            Я согласен с&nbsp;
                            <Link
                                to="/docx/oferta.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.agreementLink}
                                onClick={e => e.stopPropagation()}
                            >
                                условиями&nbsp;
                            </Link>
                            подписки
                        </span>
                    </Checkbox>
                    {agreementErrorText && (
                        <p id="renew-agreement-error" className={styles.agreementErrorText} role="alert">
                            {agreementErrorText}
                        </p>
                    )}
                </div>
            </div>

            <div className={styles.footer}>
                {payError && (
                    <p className={styles.payErrorText} role="alert">
                        {payError}
                    </p>
                )}
                <ButtonPrimary type="button" className="w-100" onClick={handlePay} disabled={isLoading}>
                    {isLoading ? 'Переход к оплате…' : <>Оплатить&nbsp;•&nbsp;{price}</>}
                </ButtonPrimary>
                <ButtonSecondary type="button" className="w-100" onClick={onClose} disabled={isLoading}>
                    Отмена
                </ButtonSecondary>
            </div>
        </>
    );
};

export const RenewSubscriptionModal = (props: RenewSubscriptionModalProps) => (
    <Form mode="onChange" defaultValues={{agreement: false}} className={styles.modal}>
        <RenewSubscriptionModalForm {...props}/>
    </Form>
);
